/**
 * helpers/masks.ts
 *
 * Máscaras automáticas via atributo data-mask nos inputs.
 *
 * Uso:
 *   <input data-mask="cpf"   />  →  000.000.000-00
 *   <input data-mask="cnpj"  />  →  00.000.000/0000-00
 *   <input data-mask="phone" />  →  (00) 00000-0000
 *   <input data-mask="cep"   />  →  00000-000
 */

type MaskType = 'cpf' | 'cnpj' | 'phone' | 'telefone' | 'cep' | 'money';

const formatters: Record<MaskType, (value: string) => string> = {
    cpf(value) {
        const d = digits(value).slice(0, 11);
        if (d.length <= 3) return d;
        if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
        if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
        return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
    },

    cnpj(value) {
        const d = digits(value).slice(0, 14);
        if (d.length <= 2) return d;
        if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
        if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
        if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
        return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
    },

    phone(value) {
        const d = digits(value).slice(0, 11);
        if (d.length <= 2) return d.length ? `(${d}` : '';
        if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
        return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    },

    telefone(value) {
        const d = digits(value).slice(0, 11);
        if (d.length <= 2) return d.length ? `(${d}` : '';
        if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
        return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    },

    cep(value) {
        const d = digits(value).slice(0, 8);
        if (d.length <= 5) return d;
        return `${d.slice(0, 5)}-${d.slice(5)}`;
    },

    money(value) {
        // Mantém só dígitos, interpreta como centavos
        // Ex: "12345" → 123,45 → "123,45"
        const d = digits(value).replace(/^0+/, '') || '0';
        const cents = d.padStart(3, '0');
        const intPart = cents.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const decPart = cents.slice(-2);
        return `${intPart},${decPart}`;
    },
};

// ── Money inputs com hidden field ─────────────────────────────────────────────

function moneyFormat(n: number): string {
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function moneyParse(display: string): number {
    // "1.000,50" → 1000.50  |  "1000,50" → 1000.50  |  "1000" → 1000
    const clean = display.replace(/\./g, '').replace(',', '.');
    return parseFloat(clean) || 0;
}

// Servidor usa pt-BR (ponto = separador de milhar, vírgula = decimal).
// O hidden deve enviar "1000,00" para o model binder parsear corretamente.
function toHiddenValue(n: number): string {
    return n.toFixed(2).replace('.', ',');
}

function syncHidden(hidden: HTMLInputElement, n: number): void {
    hidden.value = toHiddenValue(n);
    hidden.dispatchEvent(new Event('input'));
}

/** Seta o valor de um campo monetário (visível + hidden) via JS externo. */
export function setMoneyField(visibleId: string, rawDecimal: number | string): void {
    const visible = document.getElementById(visibleId) as HTMLInputElement | null;
    if (!visible) return;
    const hiddenId = visible.dataset.currency;
    const hidden = hiddenId ? document.getElementById(hiddenId) as HTMLInputElement | null : null;
    const n = typeof rawDecimal === 'string' ? parseFloat(rawDecimal) : rawDecimal;
    if (isNaN(n)) return;
    visible.value = moneyFormat(n);
    if (hidden) syncHidden(hidden, n);
}

/**
 * Inicializa inputs de moeda: par <input type="text" data-currency="hiddenId"> +
 * <input type="hidden" id="hiddenId" asp-for="...">
 *
 * UX: o usuário digita "1000" ou "1000,50" livremente; ao sair do campo
 * o valor é formatado como "1.000,00". O hidden guarda o decimal invariante ("1000.00").
 */
export function initMoneyInputs(root: ParentNode = document): void {
    root.querySelectorAll<HTMLInputElement>('[data-currency]').forEach(visible => {
        const hiddenId = visible.dataset.currency!;
        const hidden = document.getElementById(hiddenId) as HTMLInputElement | null;
        if (!hidden) return;

        // Inicializa display a partir do valor do hidden
        const initVal = parseFloat(hidden.value);
        if (!isNaN(initVal) && initVal !== 0) {
            visible.value = moneyFormat(initVal);
        }

        // Enquanto digita: permite apenas dígitos e uma vírgula (máx 2 casas decimais)
        visible.addEventListener('input', () => {
            let v = visible.value.replace(/[^\d,]/g, '');
            const parts = v.split(',');
            if (parts.length > 2) v = parts[0] + ',' + parts.slice(1).join('');
            if (parts.length === 2) v = parts[0] + ',' + parts[1].slice(0, 2);
            visible.value = v;
            syncHidden(hidden, moneyParse(v));
        });

        // Ao sair: formata com separadores de milhar
        visible.addEventListener('blur', () => {
            const n = moneyParse(visible.value);
            visible.value = n > 0 ? moneyFormat(n) : '';
            hidden.value = toHiddenValue(n);
        });

        // Ao entrar: remove formatação para edição limpa ("1.000,00" → "1000,00")
        visible.addEventListener('focus', () => {
            const n = parseFloat(hidden.value) || 0;
            if (n > 0) {
                visible.value = n % 1 === 0
                    ? String(n)
                    : n.toFixed(2).replace('.', ',');
            }
            visible.select();
        });
    });
}

function digits(value: string): string {
    return value.replace(/\D/g, '');
}

function applyMask(input: HTMLInputElement, type: MaskType): void {
    const formatter = formatters[type];
    if (!formatter) return;

    input.addEventListener('input', () => {
        const pos = input.selectionStart ?? 0;
        const prev = input.value;
        const formatted = formatter(input.value);
        input.value = formatted;

        // Mantém o cursor na posição correta após a formatação
        const diff = formatted.length - prev.length;
        input.setSelectionRange(pos + diff, pos + diff);
    });

    // Formata o valor já existente ao inicializar
    if (input.value) {
        input.value = formatter(input.value);
    }
}

export function initMasks(root: ParentNode = document): void {
    root.querySelectorAll<HTMLInputElement>('[data-mask]').forEach(input => {
        const type = input.dataset.mask as MaskType;
        if (type in formatters) {
            applyMask(input, type);
        }
    });
}
